/**
 * The shared edit layer (spec 08-PRV §5.2): binds the pure ops-log transforms in
 * {@link module:edit-ops} to an INJECTED working-copy atom, so every gesture — single
 * drag, stacked pick, bulk shift, revert at four granularities — is a browser-local
 * transform of that atom with no server call.
 *
 * The transforms above the commit (drag clamping, stacked picking, bulk-shift
 * eligibility, revert granularities) are IDENTICAL on both surfaces by construction:
 *  - the eval dialog ({@link useScheduleEdits}) injects its browser-local working-log
 *    atom and adds the Calculate commit (seed / rebaseline / wire serialization);
 *  - the DPS preview ({@link useEphemeralSchedule}) injects a form-scoped ephemeral
 *    atom and never persists.
 *
 * Only the atom (and what the caller does with it) differs; the edit behavior cannot
 * drift between the two surfaces because there is one implementation of it here.
 */

import { useCallback } from 'react';
import { type PrimitiveAtom, useAtom } from 'jotai';
import { MILESTONE_ORDER } from './constants';
import {
  applyBulkShift,
  applyCsvWrites,
  applyDateOverride,
  applyDragShift,
  editedDateCount as countEditedDates,
  revert,
} from './edit-ops';
import { overlayRowDates } from './utils';
import type {
  BulkShiftResult,
  DateOverrideWrite,
  MergedDate,
  MilestoneKey,
  RevertScope,
  ScheduleOverrideEntry,
  ScheduleRow,
  SlotId,
} from './types';

export interface EditLayerApi {
  /** The current working copy of the override ops-log. */
  workingEntries: ScheduleOverrideEntry[];
  /** How many date overrides the working copy carries (non-date fields excluded). */
  editedDateCount: number;
  /** Appends a single date override (drag / stacked pick / table cell edit). */
  applyDrag: (slotId: SlotId, field: MilestoneKey, isoDay: string) => void;
  /**
   * Multi-marker drag: commits the grabbed marker's new date AND shifts every other
   * selected point by the same delta, in ONE working-copy transform. Each shifted point
   * is clamped to its own lifecycle-order bounds; locked points are skipped.
   */
  applyMultiDrag: (
    rows: ScheduleRow[],
    primary: { slotId: SlotId; field: MilestoneKey; iso: string },
    others: Array<{ slotId: SlotId; field: MilestoneKey }>,
    deltaMs: number
  ) => void;
  /** Merges a validated batch of CSV writes (value = set, null = revert) in one commit. */
  applyCsvWrites: (writes: DateOverrideWrite[]) => void;
  /** Relative-month bulk shift over selected points; returns the applied/skip summary. */
  bulkShift: (
    rows: ScheduleRow[],
    points: Array<{ slotId: SlotId; field: MilestoneKey }>,
    offsetMonths: number
  ) => BulkShiftResult;
  /** Reverts at milestone / slot / selection / whole-schedule granularity. */
  revertScope: (scope: RevertScope) => void;
  /** The row's effective dates (base overlaid with the working overrides). */
  effectiveDates: (row: ScheduleRow) => Record<MilestoneKey, MergedDate>;
  /** Replaces the whole working copy (seed / clear); consumers own their commit step. */
  setEntries: (
    next:
      | ScheduleOverrideEntry[]
      | ((prev: ScheduleOverrideEntry[]) => ScheduleOverrideEntry[])
  ) => void;
}

/**
 * The shared edit layer over an injected working-copy atom. All logic is delegated to
 * the pure transforms so it is exercised without React in the ops tests, and identical
 * on every surface that injects an atom.
 *
 * @param {PrimitiveAtom<ScheduleOverrideEntry[]>} entriesAtom - The working-copy atom.
 * @return {EditLayerApi} The transform-bound edit API.
 */
export function useEditLayer(
  entriesAtom: PrimitiveAtom<ScheduleOverrideEntry[]>
): EditLayerApi {
  const [workingEntries, setWorkingEntries] = useAtom(entriesAtom);

  const applyDrag = useCallback(
    (slotId: SlotId, field: MilestoneKey, isoDay: string) => {
      setWorkingEntries((prev) =>
        applyDateOverride(prev, slotId, field, isoDay)
      );
    },
    [setWorkingEntries]
  );

  const applyMultiDrag = useCallback(
    (
      rows: ScheduleRow[],
      primary: { slotId: SlotId; field: MilestoneKey; iso: string },
      others: Array<{ slotId: SlotId; field: MilestoneKey }>,
      deltaMs: number
    ) => {
      setWorkingEntries((prev) => {
        // The grabbed marker lands on its own clamped ISO (exactly what the user dragged);
        // the rest shift by the same delta, each clamped to its own lifecycle bounds.
        const withPrimary = applyDateOverride(
          prev,
          primary.slotId,
          primary.field,
          primary.iso
        );
        return applyDragShift(
          withPrimary,
          rows,
          others,
          deltaMs,
          MILESTONE_ORDER
        );
      });
    },
    [setWorkingEntries]
  );

  const applyCsvWritesCb = useCallback(
    (writes: DateOverrideWrite[]) => {
      setWorkingEntries((prev) => applyCsvWrites(prev, writes));
    },
    [setWorkingEntries]
  );

  const bulkShift = useCallback(
    (
      rows: ScheduleRow[],
      points: Array<{ slotId: SlotId; field: MilestoneKey }>,
      offsetMonths: number
    ): BulkShiftResult => {
      const { entries, result } = applyBulkShift(
        workingEntries,
        rows,
        points,
        offsetMonths,
        MILESTONE_ORDER
      );
      setWorkingEntries(entries);
      return result;
    },
    [workingEntries, setWorkingEntries]
  );

  const revertScope = useCallback(
    (scope: RevertScope) => {
      setWorkingEntries((prev) => revert(prev, scope));
    },
    [setWorkingEntries]
  );

  const effectiveDates = useCallback(
    (row: ScheduleRow) => overlayRowDates(row, workingEntries),
    [workingEntries]
  );

  return {
    workingEntries,
    editedDateCount: countEditedDates(workingEntries),
    applyDrag,
    applyMultiDrag,
    applyCsvWrites: applyCsvWritesCb,
    bulkShift,
    revertScope,
    effectiveDates,
    setEntries: setWorkingEntries,
  };
}
